import useCustomMutation from "@/composables/useCustomMutation";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useCardsStore } from "@/stores/cards";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { useToast } from "vue-toastification";
import { Card } from "../interfaces/Card";

interface CreateParams {
  number: string;
  fullName: string;
  expMonth: number;
  expYear: number;
  cvv: string;
  brand: string;
  cardeableId: string;
}

interface Params extends Omit<Partial<Card>, "id"> {
  id?: string | number;
  includes?: string[];
  active?: string;
  inactive?: string;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

interface UpdateParams extends Params {}

const getCards = async (params?: Params) => {
  if (!params) return;
  const { data } = await axios.get<Card[]>(`/api/internal/card`, {
    params,
  });
  return data;
};

const createCard = async (params: CreateParams) => {
  const { data } = await axios.post<Card>(`api/internal/card`, params);
  return data;
};

const updateCard = async (params: UpdateParams) => {
  const { data } = await axios.put<Card>(
    `api/internal/card/${params.id}`,
    params,
  );
  return data;
};

const deleteCard = async (params: Params) => {
  const { data } = await axios.delete(`api/internal/card/${params.id}`);
  return data;
};

const restoreCard = async (params: Params) => {
  const { data } = await axios.post(`api/internal/card/restore/${params.id}`);
  return data;
};

const useCard = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const { setActiveCards, setInactiveCards } = useCardsStore();
  const { activeCards, inactiveCards } = storeToRefs(useCardsStore());

  const {
    data: activeData,
    isLoading: activeIsLoading,
    refetch: activeRefetch,
    isFetching: activeIsFetching,
  } = useQuery(
    ["ActiveCards"],
    () => getCards(params),
    {
      enabled: params ? immediate : false,
      retry: false,
    },
  );

  const {
    data: inactiveData,
    isLoading: inactiveIsLoading,
    refetch: inactiveRefetch,
    isFetching: inactiveIsFetching,
  } = useQuery(
    ["InactiveCards"],
    () => getCards({ ...params, inactive: "active"}),
    {
      enabled: params ? immediate : false,
      retry: false,
    },
  );

  const createMutation = useMutation(
    (mutationParams: MutationParams<CreateParams>) =>
      createCard(mutationParams.params),
    useCustomMutation(toast, "create"),
  );

  const updateMutation = useMutation(
    (mutationParams: MutationParams<UpdateParams>) =>
      updateCard(mutationParams.params),
    useCustomMutation(toast, "update"),
  );

  const deleteMutation = useMutation(
    (mutationParams: MutationParams<Params>) =>
      deleteCard(mutationParams.params),
    useCustomMutation(toast, "delete"),
  );
  const restoreMutation = useMutation(
    (mutationParams: MutationParams<Params>) =>
      restoreCard(mutationParams.params),
    useCustomMutation(toast, "create"),
  );

  watch(activeData, () => {
    if (activeData.value) {
      setActiveCards(activeData.value);
    }
  });

  watch(inactiveData, () => {
    if (inactiveData.value) {
      setInactiveCards(inactiveData.value);
    }
  });

  const updateCardPriorities = async () => {
    try {
      const newOrder = activeCards.value
        .filter((card) => !card.deletedAt)
        .map((card) => card.id);

      await Promise.all(
        newOrder.map(async (cardId, index) => {
          await updateMutation.mutateAsync({
            params: { id: cardId, priority: index + 1 },
          });
        }),
      );

      await activeRefetch();

      toast.success("Se ha cambiado el orden de uso de las tarjetas");
    } catch (error) {
      toast.error("Error al actualizar la prioridad de las tarjetas");
      console.error("Error al actualizar la prioridad de las tarjetas:", error);
    }
  };

  return {
    activeCards,
    activeIsLoading,
    activeIsFetching,
    activeRefetch,

    inactiveCards,
    inactiveIsLoading,
    inactiveIsFetching,
    inactiveRefetch,

    updateCardPriorities,

    createCardMutation: createMutation,
    createCard: createMutation.mutate,
    createCardAsync: createMutation.mutateAsync,
    createCardLoading: createMutation.isLoading,

    updateCardMutation: updateMutation,
    updateCard: updateMutation.mutate,
    updateCardAsync: updateMutation.mutateAsync,

    deleteCardMutation: deleteMutation,
    deleteCard: deleteMutation.mutate,
    deleteCardAsync: deleteMutation.mutateAsync,

    restoreCardMutation: restoreMutation,
    restoreCard: restoreMutation.mutate,
    restoreCardAsync: restoreMutation.mutateAsync,
  };
};

export default useCard;
